import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { ProfileModelType } from '../../../domain/lti/investment-portfolio.enum';
import { MaxDecimalValidator } from '../../../components/input/validators/max-decimals-validator.directive';
import { RadioGroupComponent } from '../../../components/radio/radio-group.component';
import { NumberInputDirective } from '../../../components/input/number-input.directive';
import { ModalHeaderComponent } from '../../../components/modal/components/modal-header/modal-header.component';
import { IconComponent } from '../../../components/icon/icon.component';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { ModalHeaderCloseButtonComponent } from '../../../components/modal/components/modal-header/modal-header-close-button.component';
import { OpenWindowDirective } from '../../../components/open-window/open-window.directive';
import { ButtonComponent } from '../../../components/button/button.component';
import { FormComponent } from '../../../components/form/form.component';
import { ModalPanelComponent } from '../../../features/page/modal-panel.component';
import { DividerComponent } from '../../../components/panel/divider.component';
import { ModalHeaderButtonsComponent } from '../../../components/modal/components/modal-header/modal-header-buttons.component';
import { ModalHeaderTitleComponent } from '../../../components/modal/components/modal-header/modal-header-title.component';
import { InputGroupFieldComponent } from '../../../components/form/input-group/input-group-field.component';
import { HrefDirective } from '../../../directives/href/href.directive';
import { RadioComponent } from '../../../components/radio/radio.component';
import { AssumptionsData, RetirementGoal } from '../../../domain/lti/lti.domain';
import { ModalRef } from '../../../components/modal/modal-ref';
import { ModalCloseReason } from '../../../components/modal/modal.domain';
import { InputGroupComponent } from '../../../components/form/input-group/input-group.component';
import { InlineIconWrapperComponent } from '../../../features/inline-icon-wrapper/inline-icon-wrapper.component';
import { PercentPipe } from '../../../pipes/percent.pipe';
import { InputErrorsComponent } from '../../../components/form/input-errors/input-errors.component';
import { FormRowComponent } from '../../../components/form/row/form-row.component';
import { InputDirective } from '../../../components/input/input.directive';
import { GoalType } from '../../../domain/lti/lti.enum';
import { GoalAssumptionsTooltipComponent } from './tooltip/goal-assumptions-tooltip.component';

import { GoalAssumptionsService } from './goal-assumptions.service';

@Component({
  selector: 'td-wb-lti-goal-assumptions',
  templateUrl: 'goal-assumptions.component.html',
  styleUrls: ['goal-assumptions.component.scss'],
  imports: [
    TranslateDirective,
    ModalHeaderComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderCloseButtonComponent,
    ModalPanelComponent,
    OpenWindowDirective,
    ModalHeaderTitleComponent,
    DividerComponent,
    GoalAssumptionsTooltipComponent,
    HrefDirective,
    IconComponent,
    InlineIconWrapperComponent,
    PercentPipe,
    NgTemplateOutlet,
    FormComponent,
    FormRowComponent,
    RadioGroupComponent,
    FormsModule,
    RadioComponent,
    InputGroupComponent,
    InputGroupFieldComponent,
    InputDirective,
    NumberInputDirective,
    InputErrorsComponent,
    CurrencyPipe,
    MaxDecimalValidator,
    ButtonComponent
  ]
})

export class GoalAssumptionsComponent {
  private readonly modalRef = inject<ModalRef<GoalAssumptionsComponent>>(ModalRef);
  private readonly assumptionService = inject(GoalAssumptionsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private static readonly QPP: string = 'QPP';
  private static readonly CPP: string = 'CPP';
  private static readonly NO: string = 'No';
  private static readonly YES: string = 'Yes';

  readonly assumptionData = input<AssumptionsData>(undefined);

  annualReturns: number;
  standardDeviation: number;
  includeCPPQPP: string;
  includeOAS: string;
  goalType: GoalType;
  profileModelType: ProfileModelType;
  averageCPPQPPAmount: number;
  averageOASAmount: number;
  additionalRetirementIncome: number;
  inflationPercentage: number;
  lifeExpectedAge: number;
  disableCPPQPPAmount: boolean;
  disableOASAmount: boolean;
  retirementAge: number;

  goalId: string;

  constructor() {
    toObservable(this.assumptionData)
      .pipe(takeUntilDestroyed())
      .subscribe((assumptionData: AssumptionsData | undefined): void => {
        if (assumptionData === undefined) {
          return;
        }

        this.applyAssumptionData(assumptionData);
        this.changeDetectorRef.markForCheck();
      });
  }

  onAssumptionResetClick(): void {
    this.assumptionService.retrieveDefaultAssumptions(GoalType.RETIREMENT).subscribe((investmentGoal: RetirementGoal): void => {
      if (investmentGoal !== undefined) {
        if (investmentGoal.includeCPP) {
          this.includeCPPQPP = GoalAssumptionsComponent.CPP;
          this.averageCPPQPPAmount = investmentGoal.annualCPPAmount;
        } else if (investmentGoal.includeQPP) {
          this.includeCPPQPP = GoalAssumptionsComponent.QPP;
          this.averageCPPQPPAmount = investmentGoal.annualQPPAmount;
        } else {
          this.includeCPPQPP = GoalAssumptionsComponent.NO;
        }
        this.disableCPPQPPAmount = !investmentGoal.includeCPP && !investmentGoal.includeQPP;
        this.disableOASAmount = !investmentGoal.includeOAS;
        this.averageOASAmount = investmentGoal.annualOASAmount;
        this.includeOAS = investmentGoal.includeOAS ? GoalAssumptionsComponent.YES : GoalAssumptionsComponent.NO;
        this.additionalRetirementIncome = investmentGoal.additionalRetirementIncome;
        this.inflationPercentage = investmentGoal.inflationRate;
        this.lifeExpectedAge = investmentGoal.lifeExpectancy;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onAssumptionSaveClick(): void {
    // call assumptions service to "set assumptions" based on assumptions object
    this.assumptionService.setAssumptions({
      includeCPP: this.includeCPPQPP === GoalAssumptionsComponent.CPP,
      includeQPP: this.includeCPPQPP === GoalAssumptionsComponent.QPP,
      includeOAS: this.includeOAS === GoalAssumptionsComponent.YES,
      additionalRetirementIncome: this.additionalRetirementIncome === undefined ? 0 : this.additionalRetirementIncome,
      inflationRate: this.inflationPercentage,
      lifeExpectancy: this.lifeExpectedAge,
      cppAmount: this.includeCPPQPP === GoalAssumptionsComponent.CPP ? this.averageCPPQPPAmount : undefined,
      qppAmount: this.includeCPPQPP === GoalAssumptionsComponent.QPP ? this.averageCPPQPPAmount : undefined,
      oasAmount: this.includeOAS === GoalAssumptionsComponent.YES ? this.averageOASAmount : undefined
    });
    this.modalRef.close(ModalCloseReason.HEADER_BUTTON);
  }

  onIncludeCPPQPPChange(): void {
    this.disableCPPQPPAmount = this.includeCPPQPP === GoalAssumptionsComponent.NO;
  }

  onIncludeOASChange(): void {
    this.disableOASAmount = this.includeOAS === GoalAssumptionsComponent.NO;
  }

  isRetirementGoal(): boolean {
    return this.goalType === GoalType.RETIREMENT;
  }

  hasInvestorProfileModel(): boolean {
    const assumptionData = this.assumptionData();
    return assumptionData !== undefined && assumptionData.investorProfileModel !== undefined;
  }

  getMinLifeExpected(): number {
    return this.retirementAge + 1;
  }

  private applyAssumptionData(assumptionData: AssumptionsData): void {
    this.goalId = assumptionData.goalId;
    this.goalType = assumptionData.subType;
    if (assumptionData.investorProfileModel !== undefined) {
      this.profileModelType = assumptionData.investorProfileModel.profileModelType;
      this.standardDeviation = assumptionData.investorProfileModel.annualPerformanceRiskPct;
      this.annualReturns = assumptionData.investorProfileModel.averageYearReturnPct;
    }
    this.averageOASAmount = assumptionData.oasAmount;
    this.inflationPercentage = assumptionData.inflationRate;
    this.lifeExpectedAge = assumptionData.lifeExpectancy;
    this.retirementAge = assumptionData.retirementAge;
    this.additionalRetirementIncome = assumptionData.additionalRetirementIncome;
    this.includeOAS = assumptionData.includeOAS ? GoalAssumptionsComponent.YES : GoalAssumptionsComponent.NO;
    if (assumptionData.includeCPP) {
      this.includeCPPQPP = GoalAssumptionsComponent.CPP;
      this.averageCPPQPPAmount = assumptionData.cppAmount;
    } else if (assumptionData.includeQPP) {
      this.includeCPPQPP = GoalAssumptionsComponent.QPP;
      this.averageCPPQPPAmount = assumptionData.qppAmount;
    } else {
      this.includeCPPQPP = GoalAssumptionsComponent.NO;
    }
    this.disableCPPQPPAmount = this.includeCPPQPP === GoalAssumptionsComponent.NO;
    this.disableOASAmount = this.includeOAS === GoalAssumptionsComponent.NO;
  }
}

